import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import type { TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { Order, OrderStatus } from 'apis/OrderApi';
import { FieldSettings } from 'utils/dataQuery';
import { PartnerTypes } from 'apis/PartnerApi';
import { useIntl } from 'react-intl';

import 'components/OrderGMetrixTable/order-gmetrix-table.scss';

interface OrderGMetrixTableProps<T> {
    advancedSearchFields: FieldSettings[];
    allowExport?: boolean;
    columnData: GMetrixTableColumnProps<Order>[];
    loadingGridData: boolean;
    manualLoadGridData?: boolean;
    multiSelect?: boolean;
    onDetailsItemClick?: (dataItem: Order) => void | undefined;
    onOrderStatusChange: (status: OrderStatus, checked: boolean) => void;
    onSelectedChange?: (selected: Order[]) => void;
    orderFilterValue: { id: number; text: string };
    orders: Order[];
    orderStatuses: OrderStatus[];
    partnerType: PartnerTypes;
    processedRecievedValue: {
        id: number;
        text: string;
    }
    searchTerm: string;
    selectableRows?: boolean;
    selectedOrders?: Order[];
    setOrderFilterValue: Dispatch<
        SetStateAction<{
            id: number;
            text: string;
        }>
    >;
    setProcessedRecievedValue: Dispatch<
        SetStateAction<{
            id: number;
            text: string;
        }>
    >;
    setSearchTerm: (searchTerm: string) => void;
    toggleColumns?: boolean;
}

export function OrderGMetrixTable<T>(props: OrderGMetrixTableProps<T>) {

    const { allowExport = true, columnData, loadingGridData, multiSelect = true, onDetailsItemClick,
        onOrderStatusChange, onSelectedChange, orders, orderStatuses, partnerType, processedRecievedValue, searchTerm,
        selectableRows = true, selectedOrders, setProcessedRecievedValue, setSearchTerm, toggleColumns = true } = props;

    const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
    const { formatMessage } = useIntl();

    useEffect(() => {

        setFilteredOrders(orders);

    }, [orders]);

    const statusFilter: TableFilter = {
        label: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }),
        options: [
            {
                checked: orderStatuses.includes(OrderStatus.Pending),
                label: formatMessage({ id: 'pending_orders.abd66f4e', defaultMessage: 'Pending Orders' }),
                onSelect: (checked: boolean) => onOrderStatusChange(OrderStatus.Pending, checked),
                value: OrderStatus.Pending
            },
            {
                checked: orderStatuses.includes(OrderStatus.Processed),
                label: formatMessage({ id: 'processed_orders.44341883', defaultMessage: 'Processed Orders' }),
                onSelect: (checked: boolean) => onOrderStatusChange(OrderStatus.Processed, checked),
                value: OrderStatus.Processed
            },
            {
                checked: orderStatuses.includes(OrderStatus.Canceled),
                label: formatMessage({ id: 'cancelled_orders.68c509bc', defaultMessage: 'Cancelled Orders' }),
                onSelect: (checked: boolean) => onOrderStatusChange(OrderStatus.Canceled, checked),
                value: OrderStatus.Canceled
            }
        ],
        type: 'checkbox'
    };

    const orderTypeFilter: TableFilter = {
        label: formatMessage({ id: 'order_type.ab55ddb9', defaultMessage: 'Order Type' }),
        options: [
            {
                checked: processedRecievedValue.id === 1,
                label: formatMessage({ id: 'placed_orders.cd0237b2', defaultMessage: 'Placed Orders' }),
                onSelect: () => setProcessedRecievedValue({ id: 1, text: formatMessage({ id: 'placed_orders.cd0237b2', defaultMessage: 'Placed Orders' }) }),
                value: 1
            },
            {
                checked: processedRecievedValue.id === 2,
                label: formatMessage({ id: 'received_orders.e7308840', defaultMessage: 'Received Orders' }) || 'Received Orders',
                onSelect: () => setProcessedRecievedValue({ id: 2, text: formatMessage({ id: 'received_orders.e7308840', defaultMessage: 'Received Orders' }) || 'Received Orders' }),
                value: 2
            }
        ],
        type: 'radio'
    };

    const filters: TableFilter[] = partnerType !== PartnerTypes.TestingCenter
        ? [orderTypeFilter, statusFilter]
        : [statusFilter];

    return (
        <GMetrixTable
            allowExport={allowExport}
            bottomBarTableName={formatMessage({ id: 'orders.2fb22e52', defaultMessage: 'Orders' })}
            columnData={columnData}
            excelExportFileName={formatMessage({ id: 'orderlist.6af62335', defaultMessage: 'OrderList' })}
            filters={filters}
            loading={loadingGridData}
            multiSelect={multiSelect}
            onDetailsItemClick={onDetailsItemClick}
            onSelectedChange={onSelectedChange}
            rowData={filteredOrders}
            searchPlaceholder={formatMessage({ id: 'search_orders_by_student_item_or_status.a97bfedb', defaultMessage: 'Search orders by student, item, or status...' })}
            searchTerm={searchTerm}
            selectableRows={selectableRows}
            selected={selectedOrders}
            setSearchTerm={setSearchTerm}
            toggleColumns={toggleColumns}
        />
    );

}
